import React, { FC, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, FONTS, FONT_WEIGHTS, SPACINGS } from "../../common/consts";
import { formatLongDate } from "../../common/utils/date-time";
import { getInsightsReport } from "../../services/firebase";
import { InsightsReport } from "../../types/addons";
import MetricsPanel, { MetricSection, MetricsRow } from "../../components/Insights/MetricsPanel";
import Card from "../../components/Display/Card";
import Text, { TextVariant } from "../../components/Display/Text";
import DataGrid, { DataGridCol } from "../../components/Display/DataGrid";
import { TimeframeOption } from "./Insights";
import { ReportKey } from "./ReportNav";

const SectionHeading = styled(Text)`
  font: ${FONTS.sm};
  font-weight: ${FONT_WEIGHTS.medium};
  letter-spacing: 0.025rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0 0 ${SPACINGS.sm} ${SPACINGS.xs};
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;

const GridCard = styled(Card)`
  padding: ${SPACINGS.lg};
`;

const Stack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.xl};
`;

const usd = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v);
const pct = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "0%");
const n = (v: number) => v.toLocaleString();
const money = (value: number) => usd(value || 0);

interface UserRow {
  id: string;
  user: string;
  [key: string]: string | number;
}

const USER_COLUMNS: Record<Exclude<ReportKey, "email">, DataGridCol<UserRow>[]> = {
  profile: [
    { field: "user", headerName: "User", flex: 1.6, minWidth: 200 },
    { field: "profileRefreshes", headerName: "Profiles checked", flex: 1, minWidth: 140, type: "number" },
    { field: "profileUpdates", headerName: "Profiles updated", flex: 1, minWidth: 140, type: "number" },
    { field: "profileFieldsUpdated", headerName: "Fields changed", flex: 1, minWidth: 130, type: "number" },
    { field: "jobChangesDetected", headerName: "Job changes", flex: 1, minWidth: 120, type: "number" },
    { field: "exportRequests", headerName: "Exports", flex: 1, minWidth: 100, type: "number" },
    { field: "exportEmailsFound", headerName: "Emails found", flex: 1, minWidth: 120, type: "number" },
  ],
  phone: [
    { field: "user", headerName: "User", flex: 1.6, minWidth: 200 },
    { field: "phoneLookups", headerName: "Lookups", flex: 1, minWidth: 110, type: "number" },
    { field: "phoneLookupsFound", headerName: "Numbers found", flex: 1, minWidth: 140, type: "number" },
    { field: "phoneManualLookups", headerName: "Manual", flex: 1, minWidth: 100, type: "number" },
    { field: "phoneVerifications", headerName: "Verifications", flex: 1, minWidth: 120, type: "number" },
    { field: "phoneSpend", headerName: "Spend", flex: 1, minWidth: 110, type: "number", valueFormatter: money },
  ],
  linkedin: [
    { field: "user", headerName: "User", flex: 1.6, minWidth: 200 },
    { field: "linkedinConnectsSent", headerName: "Connects sent", flex: 1, minWidth: 130, type: "number" },
    { field: "linkedinConnectsAccepted", headerName: "Accepted", flex: 1, minWidth: 110, type: "number" },
    { field: "linkedinMessagesSent", headerName: "Messages", flex: 1, minWidth: 110, type: "number" },
    { field: "linkedinProfileViews", headerName: "Profile views", flex: 1, minWidth: 130, type: "number" },
  ],
};

interface Props {
  report: Exclude<ReportKey, "email">;
  timeframe: TimeframeOption;
  /** Hidden for the public Outreach embed, which has no signed-in admin. */
  enabled: boolean;
}

/** One report at a time: profile, phone, or LinkedIn, on the same layout as the email report. */
const ActivityPanels: FC<Props> = ({ report, timeframe, enabled }) => {
  const [data, setData] = useState<InsightsReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setIsLoading(true);
    getInsightsReport(timeframe.startDate, timeframe.endDate)
      .then((r) => {
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load this report. Please refresh the page.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, timeframe.value, timeframe.startDate, timeframe.endDate]);

  const rows: MetricsRow[] = useMemo(
    () =>
      (data?.days || []).map((d) => ({
        ...d,
        date: formatLongDate(new Date(`${d.date}T12:00:00`)),
        phoneCostUsd: d.phoneCostUsdMicros / 1_000_000,
      })),
    [data]
  );

  const t = data?.totals;

  const sections: Record<Exclude<ReportKey, "email">, MetricSection[]> = useMemo(
    () => ({
      profile: [
        {
          key: "profile",
          title: "Profile enrichment",
          stats: [
            // Checked counts every LinkedIn read, most of which find Outreach
            // already matching; updated counts the ones that changed something.
            { key: "refreshed", label: "Profiles checked", value: n(t?.profileRefreshes || 0), color: COLORS.chartEmailProspectsChecked },
            { key: "updated", label: "Profiles updated", value: n(t?.profileUpdates || 0), color: COLORS.chartEmailValid },
            { key: "fields", label: "Fields changed", value: n(t?.profileFieldsUpdated || 0), color: COLORS.chartEmailValid },
            { key: "jobs", label: "Job changes found", value: n(t?.jobChangesDetected || 0), color: COLORS.chartEmailQuestionable },
          ],
          series: [
            { key: "profileRefreshes", label: "Profiles checked", color: COLORS.chartEmailProspectsChecked },
            { key: "profileUpdates", label: "Profiles updated", color: COLORS.chartEmailValid },
            { key: "profileFieldsUpdated", label: "Fields changed", color: COLORS.chartEmailQuestionable },
            { key: "jobChangesDetected", label: "Job changes", color: COLORS.chartEmailQuestionable },
          ],
        },
        {
          key: "exports",
          title: "LinkedIn exports",
          stats: [
            { key: "exports", label: "Profiles exported", value: n(t?.exportRequests || 0), color: COLORS.chartEmailProspectsChecked },
            { key: "emails", label: `Emails found (${pct(t?.exportEmailsFound || 0, t?.exportRequests || 0)})`, value: n(t?.exportEmailsFound || 0), color: COLORS.chartEmailValid },
            { key: "enrich", label: "Enrich requests", value: n(t?.extensionEnrichRequests || 0), color: COLORS.chartEmailEnriched },
          ],
          series: [
            { key: "exportRequests", label: "Exports", color: COLORS.chartEmailProspectsChecked },
            { key: "exportEmailsFound", label: "Emails found", color: COLORS.chartEmailValid },
            { key: "extensionEnrichRequests", label: "Enrich requests", color: COLORS.chartEmailEnriched },
          ],
        },
      ],
      phone: [
        {
          key: "enrichment",
          title: "Phone enrichment",
          stats: [
            { key: "lookups", label: "Lookups", value: n(t?.phoneLookups || 0), color: COLORS.chartEmailProspectsChecked },
            { key: "found", label: `Numbers found (${pct(t?.phoneLookupsFound || 0, t?.phoneLookups || 0)})`, value: n(t?.phoneLookupsFound || 0), color: COLORS.chartEmailValid },
            { key: "manual", label: "Manual lookups", value: n(t?.phoneManualLookups || 0), color: COLORS.chartEmailEnriched },
            { key: "spend", label: "Spend", value: usd((t?.phoneCostUsdMicros || 0) / 1_000_000) },
          ],
          series: [
            { key: "phoneLookups", label: "Lookups", color: COLORS.chartEmailProspectsChecked },
            { key: "phoneLookupsFound", label: "Numbers found", color: COLORS.chartEmailValid },
            { key: "phoneManualLookups", label: "Manual", color: COLORS.chartEmailEnriched },
            { key: "phoneCostUsd", label: "Spend", color: COLORS.chartEmailQuestionable, format: usd },
          ],
        },
        {
          key: "verification",
          title: "Phone verification",
          stats: [{ key: "verifications", label: "Numbers verified", value: n(t?.phoneVerifications || 0), color: COLORS.chartEmailValid }],
          series: [{ key: "phoneVerifications", label: "Verifications", color: COLORS.chartEmailValid }],
        },
      ],
      linkedin: [
        {
          key: "outreach",
          title: "LinkedIn automation",
          stats: [
            { key: "sent", label: "Connects sent", value: n(t?.linkedinConnectsSent || 0), color: COLORS.chartEmailProspectsChecked },
            { key: "accepted", label: `Accepted (${pct(t?.linkedinConnectsAccepted || 0, t?.linkedinConnectsSent || 0)})`, value: n(t?.linkedinConnectsAccepted || 0), color: COLORS.chartEmailValid },
            { key: "messages", label: "Messages sent", value: n(t?.linkedinMessagesSent || 0), color: COLORS.chartEmailEnriched },
            { key: "replies", label: `Replies (${pct(data?.linkedin.replies || 0, t?.linkedinMessagesSent || 0)})`, value: n(data?.linkedin.replies || 0), color: COLORS.chartEmailQuestionable },
          ],
          series: [
            { key: "linkedinConnectsSent", label: "Connects sent", color: COLORS.chartEmailProspectsChecked },
            { key: "linkedinConnectsAccepted", label: "Accepted", color: COLORS.chartEmailValid },
            { key: "linkedinMessagesSent", label: "Messages", color: COLORS.chartEmailEnriched },
            { key: "linkedinProfileViews", label: "Profile views", color: COLORS.chartEmailQuestionable },
          ],
        },
        {
          key: "queue",
          title: "Automation queue",
          stats: [
            { key: "pending", label: "Queued", value: n(data?.linkedin.queue?.pending || 0) },
            { key: "done", label: "Completed", value: n(data?.linkedin.queue?.completed || 0), color: COLORS.chartEmailValid },
            { key: "failed", label: "Failed", value: n(data?.linkedin.queue?.failed || 0), color: COLORS.chartEmailInvalid },
          ],
          series: [
            { key: "linkedinMessagesSent", label: "Messages", color: COLORS.chartEmailEnriched },
            { key: "linkedinReplies", label: "Replies", color: COLORS.chartEmailQuestionable },
          ],
        },
      ],
    }),
    [data, t]
  );

  const userRows: UserRow[] = useMemo(
    () =>
      (data?.byUser || []).map((u) => ({
        ...u,
        id: u.user,
        phoneSpend: u.phoneCostUsdMicros / 1_000_000,
      })),
    [data]
  );

  if (!enabled) return null;

  const columns = USER_COLUMNS[report];
  const active = userRows.filter((r) => columns.slice(1).some((c) => Number(r[c.field as string] || 0) > 0));

  return (
    <Stack>
      {error ? (
        <Card className="p-3">
          <Text variant={TextVariant.sm}>{error}</Text>
        </Card>
      ) : (
        <MetricsPanel sections={sections[report]} data={rows} isLoading={isLoading} />
      )}
      <div>
        <SectionHeading variant={TextVariant.sm} className="text-uppercase">
          By user
        </SectionHeading>
        <GridCard>
          {active.length === 0 ? (
            <Muted variant={TextVariant.sm}>No activity from anyone in this period yet.</Muted>
          ) : (
            <DataGrid
              rows={active}
              columns={columns}
              autoHeight
              density="compact"
              hideFooter={active.length <= 10}
              disableRowSelectionOnClick
              disableColumnMenu
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25]}
            />
          )}
        </GridCard>
      </div>
    </Stack>
  );
};

export default ActivityPanels;
